import { useCallback, useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Icon } from "@common/index";
import { ComponentColor } from "@common/types";
import { usePromotionApi } from "@/hooks/apiHooks/usePromotionApi";
import { useVendorApi } from "@/hooks/apiHooks/useVendorApi";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { useMountRead } from "@/hooks/useMountRead";
import { assertArrayPayload } from "@utils/apiPayload";
import {
    PROMOTION_SLOT_LABELS,
    PromotionSlotType,
    type Promotion,
} from "@models/promotion";
import type { VendorResponse } from "@models/vendor";
import {
    DATE_MIN,
    LAST_DAY_MAX,
    START_DATE_MAX,
    promotionSchema,
    toFormValues,
    toPromotionRequest,
    type PromotionFormValues,
} from "../_helpers/promotionForm";
import { usePromotionImage } from "../_helpers/usePromotionImage";
import { PromotionImageField } from "./PromotionImageField";
import { PromotionVendorField } from "./PromotionVendorField";

const SLOTS = [
    PromotionSlotType.TopHero,
    PromotionSlotType.MidPage,
    PromotionSlotType.Tile1,
    PromotionSlotType.Tile2,
    PromotionSlotType.Tile3,
];

interface PromotionFormDialogProps {
    /** The promotion being edited, or null to create one. */
    promotion: Promotion | null;
    onClose: () => void;
    /** Called after a successful save, before the modal closes. */
    onSaved: () => void;
}

// Create and Edit share this modal (DecisionLog §4.14: modal-based edit).
export const PromotionFormDialog = ({
    promotion,
    onClose,
    onSaved,
}: PromotionFormDialogProps) => {
    const { createPromotion, updatePromotion } = usePromotionApi();
    const { getVendors } = useVendorApi();
    const image = usePromotionImage();
    const [isSaving, setIsSaving] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const fieldId = useId();
    const idFor = (field: string) => `${fieldId}-${field}`;
    // A control's aria-describedby: its error while one is shown, then its
    // hint, as ReasonField does (review F4).
    const describedBy = (...ids: (string | false | undefined)[]) =>
        ids.filter(Boolean).join(" ") || undefined;

    // The vendor list is this modal's own read, so a failure is shown in the
    // field (with Retry) and skips useApi's toast.
    const [vendors, setVendors] = useState<VendorResponse[]>([]);
    const readVendors = useCallback(
        (isCurrent: () => boolean) =>
            getVendors({ skipErrorNotification: true }).then((list) => {
                assertArrayPayload("/vendors", list);
                if (isCurrent()) setVendors(list);
            }),
        [getVendors],
    );
    const { readStatus: vendorStatus, reload: retryVendors } =
        useMountRead(readVendors);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<PromotionFormValues>({
        resolver: zodResolver(promotionSchema),
        defaultValues: toFormValues(promotion),
    });

    // Mid-save, Escape and Cancel are refused: the outcome needs an open
    // modal to land on. A backdrop press never dismisses: typed values must
    // survive a stray click.
    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing: isSaving,
        onRequestClose: onClose,
        closeOnBackdropClick: false,
    });

    const onSubmit = async (values: PromotionFormValues) => {
        const request = toPromotionRequest(
            values,
            promotion,
            image.picked?.file ?? null,
        );
        setSaveFailed(false);
        setIsSaving(true);
        try {
            if (promotion) {
                await updatePromotion(promotion.id, {
                    ...request,
                    isActive: values.isActive,
                });
            } else {
                await createPromotion(request);
            }
        } catch {
            // useApi has toasted what the API said, when it could. The modal
            // stays open with the values typed, and says the save failed.
            setSaveFailed(true);
            setIsSaving(false);
            return;
        }
        setIsSaving(false);
        onSaved();
        onClose();
    };

    // Save waits for the vendor list: until it is in, the vendor shown is
    // not the one that would be saved.
    const vendorsReady = vendorStatus === "loaded";
    const inputClass = "input input-bordered w-full bg-white text-neutral";
    const selectClass = "select select-bordered w-full bg-white text-neutral";
    const labelClass = "font-semibold block mb-1";
    const hintClass = "mt-1 text-xs text-gray-500";
    const errorClass = "mt-1 text-sm text-error";

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            {...backdropProps}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                {...panelProps}
                className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-base-100 text-neutral shadow-xl focus:outline-none"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-base-200 p-4">
                    <h2 id={titleId} className="text-2xl font-bold uppercase">
                        {promotion ? "Edit Promotion" : "New Promotion"}
                    </h2>
                    <button
                        type="button"
                        aria-label="Close"
                        disabled={isSaving}
                        onClick={onClose}
                        className="btn btn-ghost btn-sm btn-square text-neutral"
                    >
                        <Icon name="close" className="text-2xl" />
                    </button>
                </div>

                <form
                    noValidate
                    className="flex min-h-0 flex-col"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <fieldset
                        disabled={isSaving}
                        className="space-y-4 overflow-y-auto p-4"
                    >
                        <div>
                            <label htmlFor={idFor("slot")} className={labelClass}>
                                Slot
                            </label>
                            <select
                                id={idFor("slot")}
                                className={selectClass}
                                aria-invalid={errors.slot ? true : undefined}
                                aria-describedby={describedBy(
                                    errors.slot && idFor("slot-error"),
                                    idFor("slot-hint"),
                                )}
                                {...register("slot")}
                            >
                                <option value="">Choose a slot…</option>
                                {SLOTS.map((slot) => (
                                    <option key={slot} value={String(slot)}>
                                        {PROMOTION_SLOT_LABELS[slot]}
                                    </option>
                                ))}
                            </select>
                            {errors.slot && (
                                <p
                                    id={idFor("slot-error")}
                                    className={errorClass}
                                >
                                    {errors.slot.message}
                                </p>
                            )}
                            <p id={idFor("slot-hint")} className={hintClass}>
                                Each slot shows one promotion at a time. If
                                several are live in one slot, the one with the
                                latest start date shows.
                            </p>
                        </div>

                        <div>
                            <label htmlFor={idFor("label")} className={labelClass}>
                                Label
                            </label>
                            <input
                                id={idFor("label")}
                                type="text"
                                className={inputClass}
                                aria-invalid={errors.label ? true : undefined}
                                aria-describedby={describedBy(
                                    errors.label && idFor("label-error"),
                                )}
                                {...register("label")}
                            />
                            {errors.label && (
                                <p
                                    id={idFor("label-error")}
                                    className={errorClass}
                                >
                                    {errors.label.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <label
                                htmlFor={idFor("ctaLabel")}
                                className={labelClass}
                            >
                                Button label
                            </label>
                            <input
                                id={idFor("ctaLabel")}
                                type="text"
                                placeholder="Shop now"
                                className={inputClass}
                                aria-invalid={errors.ctaLabel ? true : undefined}
                                aria-describedby={describedBy(
                                    errors.ctaLabel && idFor("ctaLabel-error"),
                                    idFor("ctaLabel-hint"),
                                )}
                                {...register("ctaLabel")}
                            />
                            {errors.ctaLabel && (
                                <p
                                    id={idFor("ctaLabel-error")}
                                    className={errorClass}
                                >
                                    {errors.ctaLabel.message}
                                </p>
                            )}
                            <p id={idFor("ctaLabel-hint")} className={hintClass}>
                                Leave blank to show “Shop now”.
                            </p>
                        </div>

                        <div>
                            <label
                                htmlFor={idFor("ctaHref")}
                                className={labelClass}
                            >
                                Button link
                            </label>
                            <input
                                id={idFor("ctaHref")}
                                type="text"
                                placeholder="/c/browse/12"
                                className={inputClass}
                                aria-invalid={errors.ctaHref ? true : undefined}
                                aria-describedby={describedBy(
                                    errors.ctaHref && idFor("ctaHref-error"),
                                    idFor("ctaHref-hint"),
                                )}
                                {...register("ctaHref")}
                            />
                            {errors.ctaHref && (
                                <p
                                    id={idFor("ctaHref-error")}
                                    className={errorClass}
                                >
                                    {errors.ctaHref.message}
                                </p>
                            )}
                            <p id={idFor("ctaHref-hint")} className={hintClass}>
                                With no link, a vendor promotion's button goes
                                to the vendor's page, and a house promotion
                                shows no button.
                            </p>
                        </div>

                        <PromotionVendorField
                            id={idFor("vendor")}
                            vendors={vendors}
                            status={vendorStatus}
                            storedVendorId={promotion?.vendorId ?? null}
                            registration={register("vendorId")}
                            onRetry={() => void retryVendors()}
                        />

                        <PromotionImageField
                            storedUrl={promotion?.imageUrl ?? null}
                            picked={image.picked}
                            refusal={image.refusal}
                            onPick={image.pick}
                            onClear={image.clear}
                            disabled={isSaving}
                        />

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label
                                    htmlFor={idFor("startDate")}
                                    className={labelClass}
                                >
                                    Start date
                                </label>
                                <input
                                    id={idFor("startDate")}
                                    type="date"
                                    min={DATE_MIN}
                                    max={START_DATE_MAX}
                                    className={inputClass}
                                    aria-invalid={
                                        errors.startDate ? true : undefined
                                    }
                                    aria-describedby={describedBy(
                                        errors.startDate &&
                                            idFor("startDate-error"),
                                        idFor("dates-hint"),
                                    )}
                                    {...register("startDate")}
                                />
                                {errors.startDate && (
                                    <p
                                        id={idFor("startDate-error")}
                                        className={errorClass}
                                    >
                                        {errors.startDate.message}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label
                                    htmlFor={idFor("lastDay")}
                                    className={labelClass}
                                >
                                    Last day
                                </label>
                                <input
                                    id={idFor("lastDay")}
                                    type="date"
                                    min={DATE_MIN}
                                    max={LAST_DAY_MAX}
                                    className={inputClass}
                                    aria-invalid={
                                        errors.lastDay ? true : undefined
                                    }
                                    aria-describedby={describedBy(
                                        errors.lastDay && idFor("lastDay-error"),
                                        idFor("dates-hint"),
                                    )}
                                    {...register("lastDay")}
                                />
                                {errors.lastDay && (
                                    <p
                                        id={idFor("lastDay-error")}
                                        className={errorClass}
                                    >
                                        {errors.lastDay.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        <p id={idFor("dates-hint")} className={hintClass}>
                            Dates are in UTC.
                        </p>

                        {/* A new promotion always starts active: the create
                            takes no IsActive. */}
                        {promotion && (
                            <div>
                                <label className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-primary"
                                        aria-describedby={idFor("isActive-hint")}
                                        {...register("isActive")}
                                    />
                                    <span className="font-semibold">Active</span>
                                </label>
                                <p id={idFor("isActive-hint")} className={hintClass}>
                                    An inactive promotion never shows on the
                                    storefront.
                                </p>
                            </div>
                        )}
                    </fieldset>

                    <div className="border-t border-base-200 p-4">
                        {saveFailed && (
                            <p role="alert" className="mb-3 text-sm text-error">
                                The promotion wasn't saved. Try again.
                            </p>
                        )}
                        {vendorStatus === "failed" && (
                            <p className="mb-3 text-sm text-gray-500">
                                Save is available once the vendor list loads.
                            </p>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button
                                color={ComponentColor.Neutral}
                                disabled={isSaving}
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                color={ComponentColor.Primary}
                                type="submit"
                                loading={isSaving}
                                disabled={!vendorsReady}
                            >
                                {isSaving ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
